import type { ReactNode } from "react";

const milestones: ReactNode[] = [
  <>Full AI back-office <strong className="font-medium text-[var(--color-fg)]">live at Raymond Legal</strong></>,
  <>Phase 2 JV pilot live: <strong className="font-medium text-[var(--color-fg)]">full automation immigration service</strong> with Raymond</>,
  <>Legal agents operational across <strong className="font-medium text-[var(--color-fg)]">3+ categories</strong></>,
  <>Singapore back-office entity live with first clients</>,
  <><strong className="font-medium text-[var(--color-fg)]">20-50 firms</strong> onboarded via low-cost AI services</>,
  <>£300K-£500K ARR at 40%+ gross margin</>,
];

const terms = [
  { label: "Round", value: "~£2.1M" },
  { label: "Total raised", value: "~£2.4M" },
  { label: "Stage", value: "Seed" },
  { label: "Runway", value: "18 mo" },
];

const funds = [
  { label: "Engineering & product", pct: 40, color: "var(--color-accent)" },
  { label: "Partnership deployment", pct: 25, color: "#818cf8" },
  { label: "Agent evolution & benchmarking", pct: 20, color: "#a5b4fc" },
  { label: "Operations & compliance", pct: 15, color: "#94a3b8" },
];

const risks: { risk: string; mitigant: ReactNode }[] = [
  {
    risk: "Partner concentration",
    mitigant: <>4 anchor partners across 2 jurisdictions plus 20-50 additional firm relationships via low-cost AI services.</>,
  },
  {
    risk: "AI accuracy in new verticals",
    mitigant: <>Benchmark framework validates before live deployment.</>,
  },
  {
    risk: "Regulatory complexity",
    mitigant: <>Experienced partners in each jurisdiction; phased approach.</>,
  },
];

export function V3TheAskSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <div className="mb-3">
        <h2 className="text-[28px] font-light tracking-tight mb-2 font-[family-name:var(--font-heading)]">
          ~£2.1M Seed <span className="text-[var(--color-fg-tertiary)] text-[20px]">(≈$2.7M)</span>.
        </h2>
        <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">
          <strong className="font-medium text-[var(--color-fg)]">Pre-seed proved:</strong> we can build. Immigration AI agent live, 15 clients, deployed in AU/NZ, £250K raised.
        </p>
        <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">
          <strong className="font-medium text-[var(--color-fg)]">Seed proves:</strong> we can deliver. Deep AI back-office with anchor partners. Wide pipeline through low-cost AI services.
        </p>
      </div>

      <div className="flex flex-row gap-5 mb-3">
        {/* Left: Milestones + capital efficiency note */}
        <div className="flex-1 flex flex-col">
          <div className="text-[11px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.1em] mb-3 pb-2 border-b border-[var(--color-border)]">
            12-Month Milestones
          </div>
          <div className="pl-5">
            <div className="flex flex-col gap-2">
              {milestones.map((m, i) => (
                <div key={i} className="flex items-start gap-3 relative">
                  <div className="absolute left-[-17px] top-[5px] w-2 h-2 rounded-full shrink-0 z-[1] bg-[var(--color-accent)]" />
                  {i < milestones.length - 1 && <div className="absolute left-[-13px] top-[12px] bottom-[-12px] w-px bg-[var(--color-border)]" />}
                  <span className="text-[11px] text-[var(--color-fg-secondary)] leading-relaxed">{m}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] leading-relaxed text-[var(--color-fg-secondary)] font-light mt-4 pt-3 border-t border-dashed border-[var(--color-border)]">
            This capital builds a <strong className="font-medium text-[var(--color-fg)]">replicable back-office structure</strong>, cross-vertical agent capabilities, and partnerships in two jurisdictions. <strong className="font-medium text-[var(--color-fg)]">The ARR is the proof point. The infrastructure is the asset.</strong>
          </p>
        </div>

        {/* Right: Use of funds + risks */}
        <div className="flex-1">
          <div className="text-[11px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-accent)] uppercase tracking-[0.1em] mb-3 pb-2 border-b border-[var(--color-border)]">
            Use of Funds
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-[110px] h-[110px] rounded-full shrink-0"
              style={{ background: "conic-gradient(var(--color-accent) 0% 40%, #818cf8 40% 65%, #a5b4fc 65% 85%, #94a3b8 85% 100%)" }}
            />
            <div className="flex flex-col gap-1.5 flex-1">
              {funds.map((f) => (
                <div key={f.label} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: f.color }} />
                  <div className="text-[11px] font-bold w-[28px]">{f.pct}%</div>
                  <div className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[10px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-fg-tertiary)] uppercase tracking-[0.1em] mb-2">
            Risks &amp; mitigants
          </div>
          <div className="space-y-1">
            {risks.map((r) => (
              <div key={r.risk} className="text-[10px] leading-relaxed">
                <span className="text-[var(--color-fg)] font-medium">{r.risk}:</span>{" "}
                <span className="text-[var(--color-fg-secondary)]">{r.mitigant}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <div className="flex justify-between font-[family-name:var(--font-mono)] text-[11px]">
          {terms.map((t) => (
            <div key={t.label} className="text-center">
              <div className="text-[16px] font-bold text-[var(--color-fg)] font-[family-name:var(--font-heading)]">{t.value}</div>
              <div className="text-[var(--color-fg-tertiary)] uppercase tracking-wider">{t.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
